import { useRef, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Upload, Download, CheckCircle2, AlertTriangle, FileText } from "lucide-react";

export default function LeadImportDialog({ open, onOpenChange, onImported }) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [fileName, setFileName] = useState("");

  const reset = () => { setResult(null); setFileName(""); if (inputRef.current) inputRef.current.value = ""; };

  const upload = async () => {
    const file = inputRef.current?.files?.[0];
    if (!file) { toast.error("Choose a CSV or Excel file first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/leads/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      if (data.created) toast.success(`${data.created} leads imported`);
      if (data.created) onImported?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const downloadTemplate = async () => {
    try {
      const resp = await api.get("/leads/import/template", { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a"); a.href = url; a.download = "leads_template.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Template download failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="bg-[#FFFFFF] border-[#E4E7EC] max-w-xl" data-testid="lead-import-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2"><Upload className="w-4 h-4 text-orange-400" /> Import Leads from CSV or Excel</DialogTitle>
          <DialogDescription className="text-neutral-600 text-xs">
            Bulk-upload a spreadsheet. Required column: <span className="text-orange-700 font-mono">lead_name</span>. Services can be semicolon or comma separated.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <button onClick={downloadTemplate} data-testid="download-template-btn" className="w-full flex items-center justify-between px-4 py-2.5 rounded-lg border border-[#E4E7EC] bg-[#FFFFFF] hover:border-orange-500/40 transition-colors">
            <span className="flex items-center gap-2 text-sm"><Download className="w-4 h-4 text-orange-400" /> Download CSV template</span>
            <span className="text-[10px] font-mono text-neutral-500">leads_template.csv</span>
          </button>

          <div className="border-2 border-dashed border-[#E4E7EC] rounded-lg p-6 text-center hover:border-orange-500/40 transition-colors">
            <input ref={inputRef} type="file" accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={e => setFileName(e.target.files?.[0]?.name || "")} data-testid="import-file-input" className="hidden" />
            <FileText className="w-8 h-8 mx-auto mb-2 text-neutral-500" />
            <button onClick={() => inputRef.current?.click()} data-testid="pick-file-btn" className="text-sm text-orange-700 hover:text-orange-700 font-mono uppercase tracking-wider">
              {fileName || "Choose CSV or Excel (.xlsx) file"}
            </button>
            {fileName && <div className="text-[10px] font-mono text-neutral-500 mt-1">Ready to import</div>}
          </div>

          {result && (
            <div className="rounded-lg border border-[#E4E7EC] bg-[#FFFFFF] p-4 space-y-2" data-testid="import-result">
              <div className="flex items-center gap-4 text-sm flex-wrap">
                <span className="flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-4 h-4" /> <strong>{result.created}</strong> created</span>
                {result.skipped > 0 && <span className="flex items-center gap-1 text-amber-700"><AlertTriangle className="w-4 h-4" /> <strong>{result.skipped}</strong> skipped</span>}
              </div>
              {result.errors?.length > 0 && (
                <div className="max-h-32 overflow-y-auto space-y-1 pt-2 border-t border-[#E4E7EC]">
                  {result.errors.map((e, i) => (
                    <div key={i} className="text-[11px] font-mono text-rose-700">Row {e.row}: {e.reason}</div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="import-close">Close</Button>
          <Button onClick={upload} disabled={busy || !fileName} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="import-upload-btn">
            {busy ? "Importing..." : "Import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
